import { Link, NavLink, Outlet, useOutletContext, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Role } from '@ready/shared';
import { useApi, type Org, type TreeUsage } from '@/lib/api';
import { isAuthor, isManager } from '@/lib/org-roles';
import { ORG_TYPE_LABELS, ROLE_LABELS } from '@/lib/labels';
import { Badge, ErrorText } from '@/components/ui';

export interface OrgContext {
  org: Org;
  usage: TreeUsage | undefined;
  /** The viewer's own role here; null when viewing as an admin of a parent org. */
  role: Role | null;
  canManage: boolean;
}

export const useOrgContext = () => useOutletContext<OrgContext>();

/**
 * Shell for one organization. Members see Overview and Members; admins also
 * get Invite members and Settings. Child organizations shows when the plan
 * allows them or some exist.
 */
export function OrgLayout() {
  const { orgId = '' } = useParams();
  const api = useApi();
  const org = useQuery({ queryKey: ['org', orgId], queryFn: () => api.org(orgId) });
  const usage = useQuery({ queryKey: ['usage', orgId], queryFn: () => api.usage(orgId) });
  const myOrgs = useQuery({ queryKey: ['myOrgs'], queryFn: api.myOrgs });

  if (org.isPending || myOrgs.isPending)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (org.isError) return <ErrorText error={org.error} />;

  const role = myOrgs.data?.orgs.find((o) => o.id === orgId)?.role ?? null;
  const canManage = isManager(role);
  const o = org.data;
  const showChildren = Boolean(
    usage.data && (usage.data.limits.maxChildOrgs > 0 || usage.data.usage.maxChildOrgs > 0),
  );

  const tabs = [
    { to: '', label: 'Overview', end: true, show: true },
    { to: 'members', label: 'Members', show: true },
    { to: 'quizzes', label: 'Quizzes', show: isAuthor(role) },
    { to: 'reports', label: 'Reports', show: canManage || role === null },
    { to: 'invites', label: 'Invite members', show: canManage },
    { to: 'children', label: 'Child organizations', show: showChildren && o.depth < 2 },
    { to: 'settings', label: 'Settings', show: canManage },
  ].filter((t) => t.show);

  return (
    <div>
      {o.parentId && (
        <Link
          to={`/organizations/${o.parentId}`}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← Parent organization
        </Link>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{o.name}</h1>
        <Badge>{ORG_TYPE_LABELS[o.type]}</Badge>
        {role ? <Badge>{ROLE_LABELS[role]}</Badge> : <Badge>Viewing as parent admin</Badge>}
      </div>
      <nav className="mt-6 flex flex-wrap gap-1 border-b border-border">
        {tabs.map((t) => (
          <NavLink
            key={t.label}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              `-mb-px border-b-2 px-3 py-2 text-sm ${isActive ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
      <div className="pt-6">
        <Outlet context={{ org: o, usage: usage.data, role, canManage } satisfies OrgContext} />
      </div>
    </div>
  );
}
